/**
 * BodyMap — mapa corporal persistente del paciente (perfil fisioterapia).
 *
 * Dos siluetas humanas (vista frontal y vista lateral) sobre las que el
 * fisioterapeuta marca puntos haciendo clic (rojo = dolor/lesión,
 * amarillo = zona en tratamiento), con una nota por punto y anotaciones
 * generales.
 *
 * Se guarda en el propio paciente (`patient.body_map`) — mismo patrón que el
 * mapa facial: campos extra del modelo, PUT normal, coordenadas en % (0..100)
 * relativas a cada vista para ser independientes del tamaño en pantalla.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, MapPin, ChevronDown, ChevronUp, Eraser } from 'lucide-react';
import { api, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const COLORS = { rojo: '#e11d48', amarillo: '#f59e0b' };
const SIL_FILL = '#eef2f1';
const SIL_STROKE = '#7c9cb8';

/** Silueta humana frontal (esquemática, suficiente para localizar zonas). */
function FrontSilhouette() {
  return (
    <svg viewBox="0 0 220 460" className="w-full block pointer-events-none select-none">
      <g fill={SIL_FILL} stroke={SIL_STROKE} strokeWidth="2">
        {/* cabeza + cuello */}
        <ellipse cx="110" cy="36" rx="24" ry="28" />
        <rect x="99" y="60" width="22" height="16" rx="7" />
        {/* tronco */}
        <path d="M70 80 Q110 70 150 80 L154 150 Q152 176 142 192 L140 236 Q110 248 80 236 L78 192 Q68 176 66 150 Z" />
        {/* brazos */}
        <rect x="42" y="86" width="20" height="118" rx="10" transform="rotate(9 52 145)" />
        <rect x="158" y="86" width="20" height="118" rx="10" transform="rotate(-9 168 145)" />
        {/* manos */}
        <ellipse cx="36" cy="216" rx="9" ry="12" />
        <ellipse cx="184" cy="216" rx="9" ry="12" />
        {/* piernas */}
        <rect x="82" y="242" width="25" height="160" rx="12" />
        <rect x="113" y="242" width="25" height="160" rx="12" />
        {/* pies */}
        <ellipse cx="92" cy="414" rx="15" ry="9" />
        <ellipse cx="128" cy="414" rx="15" ry="9" />
      </g>
    </svg>
  );
}

/** Silueta humana lateral (perfil, esquemática). */
function SideSilhouette() {
  return (
    <svg viewBox="0 0 150 460" className="w-full block pointer-events-none select-none">
      <g fill={SIL_FILL} stroke={SIL_STROKE} strokeWidth="2">
        {/* cabeza (con nariz insinuada) + cuello */}
        <path d="M60 12 Q92 8 96 38 Q97 48 90 54 L94 60 Q95 64 90 64 L86 64 Q84 70 76 70 Q58 70 54 50 Q50 22 60 12 Z" />
        <path d="M68 68 L84 66 L84 82 L66 84 Z" />
        {/* tronco con curva dorsal y glúteo */}
        <path d="M66 84 Q94 88 96 116 L98 168 Q100 190 92 206 L92 232 Q106 240 104 262 Q102 280 88 282 L64 282 Q52 250 56 208 Q48 160 54 120 Q56 94 66 84 Z" />
        {/* brazo (pegado al cuerpo) */}
        <rect x="66" y="96" width="18" height="112" rx="9" transform="rotate(4 75 152)" />
        <ellipse cx="80" cy="218" rx="8" ry="11" />
        {/* pierna */}
        <path d="M64 280 L96 282 Q98 330 90 370 L88 404 L64 404 Q58 340 60 300 Z" />
        {/* pie hacia delante */}
        <path d="M64 404 L88 404 L88 414 Q112 414 114 422 Q114 428 104 428 L64 428 Z" />
      </g>
    </svg>
  );
}

const VIEWS = [
  { key: 'front', label: 'Frontal', Comp: FrontSilhouette },
  { key: 'side', label: 'Lateral', Comp: SideSilhouette },
];

export default function BodyMap({ patient, onPatientUpdated }) {
  const { user } = useAuth();
  const bm = patient.body_map || {};
  const [markers, setMarkers] = useState(bm.markers || []);
  const [notes, setNotes] = useState(bm.notes || '');
  const [color, setColor] = useState('rojo');
  const [open, setOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const boxRefs = { front: useRef(null), side: useRef(null) };

  useEffect(() => {
    const b = patient.body_map || {};
    setMarkers(b.markers || []);
    setNotes(b.notes || '');
    setDirty(false);
  }, [patient.id, patient.body_map]);

  const meta = bm.updated_at
    ? `Última edición: ${formatDateTime(bm.updated_at)}${bm.updated_by_name ? ` · ${bm.updated_by_name}` : ''}`
    : null;

  const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `m${Date.now()}${Math.random().toString(36).slice(2, 7)}`);

  const addMarker = (view) => (e) => {
    const box = boxRefs[view].current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setMarkers((prev) => [...prev, { id: uid(), view, x, y, color, note: '' }]);
    setDirty(true);
  };

  const setNote = (id, note) => {
    setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, note } : m)));
    setDirty(true);
  };
  const removeMarker = (id) => {
    setMarkers((prev) => prev.filter((m) => m.id !== id));
    setDirty(true);
  };
  const clearAll = () => {
    if (!markers.length) return;
    if (!window.confirm('¿Borrar todos los puntos del mapa corporal?')) return;
    setMarkers([]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/patients/${patient.id}`, {
        body_map: {
          markers,
          notes,
          updated_at: new Date().toISOString(),
          updated_by_name: user?.name || '',
        },
      });
      toast.success('Mapa corporal guardado');
      setDirty(false);
      onPatientUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el mapa corporal');
    } finally {
      setSaving(false);
    }
  };

  const resumen = useMemo(() => {
    const r = markers.filter((m) => m.color === 'rojo').length;
    const a = markers.filter((m) => m.color === 'amarillo').length;
    return { r, a };
  }, [markers]);

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#2E6E5E]" /> Mapa corporal
          {markers.length > 0 && (
            // Cuántos puntos hay de cada color, con un punto del MISMO color
            // que en la silueta (antes, círculos de emoji que en los equipos
            // de la clínica salían como cuadrados; 5/10/2026).
            <span className="inline-flex items-center text-xs font-normal text-muted-foreground" data-testid="body-map-resumen">
              ({[['rojo', resumen.r, 'dolor / lesión'], ['amarillo', resumen.a, 'en tratamiento']]
                .filter(([, n]) => n > 0)
                .map(([c, n, que], i) => (
                  <React.Fragment key={c}>
                    {i > 0 && <span className="whitespace-pre"> · </span>}
                    <span className="inline-flex items-center gap-1" title={`${n} ${que}`}>
                      {n}
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block"
                        style={{ backgroundColor: COLORS[c] }}
                        aria-label={que}
                        role="img"
                      />
                    </span>
                  </React.Fragment>
                ))})
            </span>
          )}
        </span>
      }
      action={
        <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)} data-testid="body-map-toggle">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {open ? 'Ocultar' : 'Abrir'}
        </Button>
      }
    >
      {!open ? (
        <p className="text-sm text-muted-foreground">
          Marca en la silueta (frontal y lateral) las zonas de dolor o tratamiento. Pulsa «Abrir» para editarlo.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Siluetas + marcadores */}
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs text-muted-foreground">Color del punto:</span>
              {[['rojo', 'Dolor / lesión'], ['amarillo', 'En tratamiento']].map(([c, label]) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  data-testid={`body-map-color-${c}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                    color === c ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]' : 'border-[#D9E3DD]'
                  }`}
                >
                  <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: COLORS[c] }} />
                  {label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {VIEWS.map(({ key, label, Comp }) => (
                <div key={key} className="rounded-lg border border-[#D9E3DD] bg-white overflow-hidden">
                  <div className="text-center text-[11px] uppercase tracking-wider text-muted-foreground font-medium pt-1.5">
                    {label}
                  </div>
                  <div
                    ref={boxRefs[key]}
                    onClick={addMarker(key)}
                    className="relative w-full cursor-crosshair select-none"
                    data-testid={`body-map-canvas-${key}`}
                  >
                    <Comp />
                    {markers.map((m, i) => (
                      m.view === key && (
                        <span
                          key={m.id}
                          className="absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-white inline-flex items-center justify-center text-[10px] font-bold text-white shadow pointer-events-none"
                          style={{ left: `${m.x}%`, top: `${m.y}%`, backgroundColor: COLORS[m.color] || COLORS.rojo }}
                        >
                          {i + 1}
                        </span>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1.5 flex items-center gap-3">
              <span className="text-[11px] text-muted-foreground">Haz clic en la silueta para marcar</span>
              {markers.length > 0 && (
                <button type="button" onClick={clearAll} className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-800">
                  <Eraser className="w-3.5 h-3.5" /> Borrar todos los puntos
                </button>
              )}
            </div>
          </div>

          {/* Lista de puntos + anotaciones */}
          <div className="space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                Puntos marcados
              </div>
              {markers.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aún no hay puntos. Haz clic en la silueta.</p>
              ) : (
                <ol className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {markers.map((m, i) => (
                    <li key={m.id} className="flex items-center gap-2">
                      <span
                        className="w-5 h-5 shrink-0 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: COLORS[m.color] || COLORS.rojo }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-[10px] uppercase text-muted-foreground w-12 shrink-0">
                        {m.view === 'front' ? 'Front.' : 'Lat.'}
                      </span>
                      <input
                        type="text"
                        value={m.note || ''}
                        onChange={(e) => setNote(m.id, e.target.value)}
                        placeholder="Nota (ej.: cervicalgia, punto gatillo trapecio…)"
                        className="flex-1 h-7 rounded-md border border-input bg-transparent px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        data-testid={`body-map-note-${i}`}
                      />
                      <button type="button" onClick={() => removeMarker(m.id)} className="text-muted-foreground hover:text-red-700" title="Quitar punto">
                        ✕
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                Anotaciones generales
              </div>
              <Textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
                rows={3}
                placeholder="Valoración, evolución, pauta de ejercicios…"
                className="text-xs"
                data-testid="body-map-notes"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] text-muted-foreground">{meta}</span>
              <Button
                size="sm"
                onClick={save}
                disabled={saving || !dirty}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white"
                data-testid="body-map-save"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Guardar mapa corporal
              </Button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
